import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantContext } from 'src/context/useTenantContext';
import {
  orgZaloIntegration,
  orgZaloIntegrationExport,
  orgZaloIntegrationUpdate,
} from 'src/services/apis/tenantRequests';
import { ZaloIntegrationWriteDto } from 'src/services/apis/types';
import { QUERIES } from 'src/utils/react-query';

export function useZaloIntegration() {
  const { orgSlug } = useTenantContext();
  return useQuery({
    queryKey: [QUERIES.ZALO_INTEGRATION, orgSlug],
    queryFn: () => orgZaloIntegration({ orgSlug }),
    // The Zalo config form (Formik, enableReinitialize) rebuilds its fields whenever this
    // query's data changes. A window-focus refetch mid-edit would otherwise silently wipe
    // a half-typed secret the moment the tab regains focus.
    refetchOnWindowFocus: false,
  });
}

export function useSaveZaloIntegration() {
  const queryClient = useQueryClient();
  const { orgSlug } = useTenantContext();
  return useMutation({
    mutationFn: (payload: ZaloIntegrationWriteDto) => orgZaloIntegrationUpdate(payload, { orgSlug }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERIES.ZALO_INTEGRATION, orgSlug] }),
  });
}

/**
 * A mutation rather than a query: the config is fetched only when the owner asks for it, and
 * an org with no integration row answers 404 — which as a query would retry and surface as a
 * permanently errored page rather than as feedback on a click.
 */
export function useExportZaloConfig() {
  const { orgSlug } = useTenantContext();
  return useMutation({ mutationFn: () => orgZaloIntegrationExport({ orgSlug }) });
}
